import {
  supabase,
} from "../supabaseClient";

export type PrpWorkspaceReadModel = {
  profile: {
    id: string;
    full_name?: string | null;
    region?: string | null;
    status?: string | null;
    stripe_connected?: boolean;
  };

  territory?: {
    authority: string;
    scope: string;
    prp_id: string;
    region?: string | null;
    coordinate_policy: "verified_only";
    located_lead_count: number;
    located_merchant_count: number;
    unlocated_lead_count: number;
    unlocated_merchant_count: number;
  };

  metrics: {
    total_partners: number;
    active_partners: number;
    monthly_subscription_revenue: number;
    official_commission_state: string;
  };

  leads: Array<{
    id?: string | null;
    business_name?: string | null;
    contact_name?: string | null;
    city?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    geo_verified_at?: string | null;
    geo_source?: string | null;
    status?: string | null;
    stage?: string | null;
    next_action?: string | null;
    next_action_at?: string | null;
    created_at?: string | null;
  }>;

  merchants: Array<{
    id?: string | null;
    name?: string | null;
    city?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    geo_verified_at?: string | null;
    geo_source?: string | null;
    category?: string | null;
    is_approved?: boolean;
    subscription_plan?: string | null;
    subscription_status?: string | null;
    created_at?: string | null;
  }>;

  payouts: Array<{
    id?: string | null;
    status?: string | null;
    canonical_status?: string | null;
    amount?: number | string | null;
    amount_cents?: number | null;
    total_cents?: number | null;
    currency?: string | null;
    line_count?: number | null;
    created_at?: string | null;
    requested_at?: string | null;
    approved_at?: string | null;
    settled_at?: string | null;
    paid_at?: string | null;
    invalidated_at?: string | null;
    invalidation_reason?: string | null;
    financial_source?: string | null;
  }>;

  financial_statement?: {
    schema?: string;
    financial_authority?: string;
    ledger_source?: string;
    legacy_history_authority?: boolean;
    prp_id?: string;
    generated_at?: string;
    summary?: Array<Record<string, unknown>>;
    ledger_rows?: Array<Record<string, unknown>>;
    payouts?: Array<Record<string, unknown>>;
    recoveries?: Array<Record<string, unknown>>;
  };

  financial_authority?: string;
  financial_ledger_source?: string;
  legacy_history_authority?: boolean;

  team: Array<{
    id?: string | null;
    full_name?: string | null;
    email?: string | null;
    commission_rate?: number | null;
    compensation_state?: string | null;
    status?: string | null;
    created_at?: string | null;
  }>;

  activities: Array<{
    id?: string | null;
    lead_id?: string | null;
    business_id?: string | null;
    activity_type?: string | null;
    summary?: string | null;
    occurred_at?: string | null;
    next_action?: string | null;
    next_action_at?: string | null;
    created_at?: string | null;
  }>;

  summary: {
    lead_count: number;
    merchant_count: number;
    approved_merchant_count: number;
    pending_payout_count: number;
    activity_count: number;
  };

  generated_at: string;
};

export type PrpForfyMode =
  | "prepare_outreach"
  | "next_best_action"
  | "meeting_brief"
  | "follow_up_draft"
  | "pipeline_risk";

export type PrpWorkspaceAction =
  | "lead_create"
  | "lead_update_status"
  | "lead_archive"
  | "lead_restore"
  | "activity_add"
  | "forfy_result_save"
  | "team_add"
  | "team_remove"
  | "team_restore";

type PrpEdgeOptions = {
  body?: unknown;
  headers?: Record<string, string>;
};

async function invokePrpEdge(
  name: string,
  options: PrpEdgeOptions = {},
) {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) throw sessionError;

  const token =
    session?.access_token?.trim();

  if (!token) {
    throw new Error("prp_session_required");
  }

  const result =
    await supabase.functions.invoke(
      name,
      {
        body: options.body,
        headers: {
          ...(options.headers || {}),
          Authorization:
            `Bearer ${token}`,
        },
      },
    );

  if (!result.error) {
    return result;
  }

  let code =
    typeof (result.data as any)?.error === "string"
      ? String(
          (result.data as any).error,
        ).trim()
      : "";

  const context =
    (result.error as any)?.context;

  const response =
    typeof Response !== "undefined"
    && context instanceof Response
      ? context
      : typeof Response !== "undefined"
        && context?.response instanceof Response
        ? context.response
        : null;

  if (!code && response) {
    try {
      const payload =
        await response.clone().json();

      code =
        typeof payload?.error === "string"
          ? payload.error.trim()
          : "";
    } catch {
      // Preserve transport message as fallback.
    }
  }

  return {
    data: result.data,
    error:
      new Error(
        code
        || result.error.message
        || `${name}_failed`,
      ),
  };
}

export async function loadPrpWorkspace():
  Promise<PrpWorkspaceReadModel> {
  const {
    data,
    error,
  } =
    await invokePrpEdge(
        "prp-dashboard-read",
        {
          body: {},
        },
      );

  if (error) {
    throw error;
  }

  if (
    !data?.ok
    || !data?.profile
  ) {
    throw new Error(
      data?.error
      || "prp_dashboard_read_failed",
    );
  }

  return data as
    PrpWorkspaceReadModel;
}

export async function prpWorkspaceCommand(
  action: PrpWorkspaceAction,
  payload:
    Record<string, unknown> = {},
) {
  const idempotencyKey =
    typeof crypto
      !== "undefined"
      && typeof crypto.randomUUID
        === "function"
      ? crypto.randomUUID()
      : [
          Date.now(),
          Math.random()
            .toString(36)
            .slice(2),
        ].join("-");

  const {
    data,
    error,
  } =
    await invokePrpEdge(
        "prp-workspace-command",
        {
          body: {
            action,
            idempotency_key:
              idempotencyKey,
            ...payload,
          },
        },
      );

  if (error) {
    throw error;
  }

  if (!data?.ok) {
    throw new Error(
      data?.error
      || "prp_workspace_command_failed",
    );
  }

  return data as {
    ok: true;
    duplicate?: boolean;
    action: PrpWorkspaceAction;
    target_type?: string | null;
    target_id?: string | null;
    result?: Record<
      string,
      unknown
    >;
    financial_authority: false;
  };
}

export async function runPrpForfy(
  input: {
    mode: PrpForfyMode;
    target_type:
      | "overview"
      | "lead"
      | "business";
    target_id?: string | null;
    objective?: string;
    language?: "fr" | "en";
  },
) {
  const {
    data,
    error,
  } =
    await invokePrpEdge(
        "prp-forfy-copilot",
        {
          body: input,
        },
      );

  if (error) {
    throw error;
  }

  if (!data?.ok) {
    throw new Error(
      data?.error
      || "forfy_prp_failed",
    );
  }

  return data as {
    ok: true;
    result: string;
    human_confirmation_required: true;
    no_action_executed: true;
    generated_at: string;
  };
}
